import websdk from 'easemob-websdk'
import { IMProject, resolveIMConfig } from './imConfig'

export type { IMProject } from './imConfig'

export interface IMService {
  initIM: () => any
  loginIM: (username: string, token: string) => void
  loginIMWithPassword: (username: string, password: string) => void
  sendPrivateMessage: (to: string, msg: string) => void
  sendImageMessage: (to: string, fileData: any) => void
  recallMessage: (serverMsgId: string, to: string) => void
  getConn: () => any
  getProject: () => IMProject
}

function attachListeners(conn: any, project: IMProject) {
  const emit = (eventName: string, detail: any) => {
    window.dispatchEvent(
      new CustomEvent(eventName, {
        detail: { ...detail, _imProject: project },
      })
    )
  }

  conn.listen({
    onOpened: function () {
      console.log(`[IM:${project}] connected successfully`)
    },
    onClosed: function () {
      console.log(`[IM:${project}] disconnected`)
    },
    onTextMessage: function (message: any) {
      console.log(`[IM:${project}] received text message:`, message)
      emit('on-im-message', message)
    },
    onRecallMessage: function (message: any) {
      console.log(`[IM:${project}] message recall:`, message)
      emit('on-im-message-recall', message)
    },
    onError: function (error: any) {
      console.error(`[IM:${project}] error:`, error)
      const errorStr = JSON.stringify(error).toLowerCase()
      const errorMsg = (error.message || error.data || '').toString().toLowerCase()
      if (
        errorStr.includes('already logged') ||
        errorStr.includes('another device') ||
        errorStr.includes('kicked') ||
        errorStr.includes('conflict') ||
        errorMsg.includes('already logged') ||
        errorMsg.includes('another device') ||
        errorMsg.includes('kicked') ||
        errorMsg.includes('conflict')
      ) {
        emit('on-im-kicked-offline', error)
      }
    },
    onMultiDeviceEvent: function (event: any) {
      console.log(`[IM:${project}] multi device event:`, event)
      if (event && (event.operation === 'kicked' || event.operation === 'conflict')) {
        emit('on-im-kicked-offline', event)
      }
    },
  })
}

export function createIMService(project: IMProject): IMService {
  let conn: any = null

  const initIM = () => {
    if (conn) return conn

    const { appKey } = resolveIMConfig(project)
    if (!appKey) {
      console.error(`[IM:${project}] appKey 未配置，请在 imConfig.ts 中填写`)
    }

    conn = new websdk.connection({
      appKey,
      isMultiLoginSessions: true,
      https: true,
      autoReconnectNumMax: 2,
      autoReconnectInterval: 2,
      isAutoLogin: true,
    })

    attachListeners(conn, project)
    return conn
  }

  const loginIM = (username: string, token: string) => {
    if (!conn) initIM()
    conn.open({
      user: username,
      accessToken: token,
    })
  }

  const loginIMWithPassword = (username: string, password: string) => {
    if (!conn) initIM()
    conn.open({
      user: username,
      pwd: password,
    })
  }

  const sendPrivateMessage = (to: string, msg: string) => {
    if (!conn) return
    const id = conn.getUniqueId()
    const message = new websdk.message('txt', id)
    message.set({
      msg,
      to,
      chatType: 'singleChat',
      success: function (id: any, serverMsgId: any) {
        console.log(`[IM:${project}] send private text success`)
        window.dispatchEvent(
          new CustomEvent('on-im-message-sent', {
            detail: { id, serverMsgId, to, msg, from: conn.user, time: Date.now(), _imProject: project },
          })
        )
      },
      fail: function (e: any) {
        console.log(`[IM:${project}] send private text error`, e)
      },
    })
    conn.send(message.body)
  }

  const sendImageMessage = (to: string, fileData: any) => {
    if (!conn) return
    const id = conn.getUniqueId()
    const message = new websdk.message('img', id)

    if (fileData.url) {
      message.set({
        to,
        chatType: 'singleChat',
        body: {
          type: 'img',
          url: fileData.url,
          filename: fileData.filename || 'image.png',
          file_length: fileData.file_length || 0,
          width: fileData.width || 0,
          height: fileData.height || 0,
        },
        success: function (id: any, serverMsgId: any) {
          console.log(`[IM:${project}] send private image success`)
          window.dispatchEvent(
            new CustomEvent('on-im-message-sent', {
              detail: {
                id,
                serverMsgId,
                to,
                msg: '[图片]',
                url: fileData.url,
                type: 'img',
                from: conn.user,
                time: Date.now(),
                _imProject: project,
              },
            })
          )
        },
        fail: function (e: any) {
          console.log(`[IM:${project}] send private image error`, e)
        },
      })
    }

    conn.send(message.body)
  }

  const recallMessage = (serverMsgId: string, to: string) => {
    if (!conn) return
    conn.recallMessage({
      to,
      mid: serverMsgId,
      type: 'chat',
      success: function () {
        console.log(`[IM:${project}] message recall success`)
        window.dispatchEvent(
          new CustomEvent('on-im-message-recall-success', {
            detail: { mid: serverMsgId, to, _imProject: project },
          })
        )
      },
      fail: function (err: any) {
        console.log(`[IM:${project}] message recall failed`, err)
      },
    })
  }

  const getConn = () => conn

  return {
    initIM,
    loginIM,
    loginIMWithPassword,
    sendPrivateMessage,
    sendImageMessage,
    recallMessage,
    getConn,
    getProject: () => project,
  }
}

// 默认 chat 项目实例，保持向后兼容
const defaultIM = createIMService('chat')
export const initIM = defaultIM.initIM
export const loginIM = defaultIM.loginIM
export const loginIMWithPassword = defaultIM.loginIMWithPassword
export const sendPrivateMessage = defaultIM.sendPrivateMessage
export const sendImageMessage = defaultIM.sendImageMessage
export const recallMessage = defaultIM.recallMessage
export const getConn = defaultIM.getConn
